import type { Rating as FranchiseRating } from "@homeinstead/schemas";
import type React from "react";
import type { CareRecipient } from "~care/types/forms/care-seeker-lead-form";
import type { CareSeekerShallowLeadFormData } from "~care/types/forms/care-seeker-shallow-lead-form";

import { track } from "@homeinstead/analytics";
import { initialValues } from "@homeinstead/sanity/schemaTypes/trusted-home-care/initial-values";
import { EventName } from "@homeinstead/shared";
import { cn } from "@homeinstead/ui";
import Image from "next/image";
import { usePathname, useRouter } from "next/navigation";
import { Controller, useFormContext } from "react-hook-form";
import { Rating } from "~care/components/ui/Rating/Rating";
import { useSetQueryParam } from "~care/hooks/useSetQueryParam";
import { debugLog } from "~care/lib/utils/debug-utils";
import { EmployButtonTrackedClickEvent } from "~care/types/events";
import { Button } from "../../Button/Button";
import { PersonWithHeart } from "../../Icons";
import { CareRecipientButton } from "../CareRecipientButton";
import { RecipientOptions } from "./form-options";

interface CareRecipientStepProps {
  nextStep: () => void;
  className?: string;
  title?: string;
  subtitle?: string;
  isDebug?: boolean;
  franchiseRating?: FranchiseRating;
  leadSource: "RPN" | "PPC";
}

const CareRecipientStep = ({
  nextStep,
  className,
  isDebug,
  title,
  subtitle,
  franchiseRating,
  leadSource,
}: CareRecipientStepProps) => {
  const { control } = useFormContext<CareSeekerShallowLeadFormData>();
  const createQueryString = useSetQueryParam();
  const pathname = usePathname();
  const router = useRouter();

  const trackCareGiverClick = () => {
    track(
      EventName.TRACKED_CLICK,
      EmployButtonTrackedClickEvent(
        pathname,
        `${window.location.origin}${pathname}`,
      ),
    );
  };

  const handleCaregiverClick = (e: React.MouseEvent) => {
    e.preventDefault();

    trackCareGiverClick();

    // Create search params manually instead of just appending to pathname
    const queryString = createQueryString("leadVariant", "CareGiver");
    const newPath = `${pathname}?${queryString}`;

    if (isDebug) {
      debugLog(`Opening caregiver drawer with path: ${newPath}`);
    }

    // Explicitly use router.push with the full path
    router.push(newPath);
  };

  const { rating, ratingCount, ratingService } = franchiseRating ?? {};

  const showApplyToBeCaregiverButton = leadSource !== "RPN";

  return (
    <div
      className={cn(
        "flex h-full flex-col gap-20 xl:flex-row xl:gap-y-4",
        className,
      )}
    >
      <div className="flex flex-col items-center gap-4 xl:items-start">
        <h1 className="mb-4 text-center font-editorial-new font-light text-1xl xl:text-left">
          {title ?? initialValues.title}
        </h1>
        <h2 className="mb-6 text-center text-md text-new-beginnings-base xl:text-left">
          {subtitle ?? initialValues.subtitle}
        </h2>
        <p className="text-md">{"I'm interested in home care services for:"}</p>
        <Controller
          control={control}
          name="step2.careFor"
          render={({ field }) => (
            <div className="flex w-full flex-col gap-4 md:w-[440px] md:gap-3">
              <input type="hidden" {...field} />
              {RecipientOptions.map((option) => {
                const Icon = option.icon;
                return (
                  <CareRecipientButton
                    careRecipient={option.value as CareRecipient}
                    className="group flex h-16 w-full justify-start gap-4 rounded-full md:h-12"
                    field={field}
                    key={option.value}
                    nextStepCallback={nextStep}
                  >
                    <Icon className="size-6 fill-friendly-base group-hover:fill-new-beginnings-base" />
                    <span>{option.label}</span>
                  </CareRecipientButton>
                );
              })}
              {/* Button hidden on desktop and moved underneath the hero image */}
              {showApplyToBeCaregiverButton && (
                <Button
                  className="mt-4 flex justify-center gap-4 px-6 text-new-beginnings-base underline hover:no-underline xl:hidden xl:justify-start"
                  onClick={handleCaregiverClick}
                  type="button"
                  variant="plain"
                >
                  <PersonWithHeart />
                  Apply to be a caregiver
                </Button>
              )}
              {!!rating && (ratingCount ?? 0) > 0 && (
                <Rating
                  className="self-center xl:mt-3"
                  rating={rating}
                  ratingCount={ratingCount!}
                  service={ratingService}
                />
              )}
            </div>
          )}
        />
      </div>
      <div className="mb-auto ml-auto hidden items-center xl:flex xl:flex-col">
        <Image
          alt={"A Home Instead Care Pro with an adult."}
          className="max-h-[540px] max-w-[524px]"
          height={848}
          src="/senior-caregiver-in-domus.webp"
          width={855}
        />
        {/* Button hidden on desktop and moved underneath the hero image */}
        {showApplyToBeCaregiverButton && (
          <Button
            className="mt-4 flex justify-center gap-4 px-6 text-new-beginnings-base underline hover:no-underline xl:justify-start"
            onClick={handleCaregiverClick}
            type="button"
            variant="plain"
          >
            <PersonWithHeart />
            Apply to be a caregiver
          </Button>
        )}
      </div>
    </div>
  );
};

export default CareRecipientStep;
